import { cartPricesAsOf, getRootUrl } from '@share-a-cart/shared-utils';
import type { ExportableCart } from '../../types/cart';
import { utils, writeFile } from 'xlsx';
import { localUrl } from '../i18n/langBasename';
// Direct import, not the src/utils barrel: that barrel re-exports this module.
import { itemOutboundHref } from '../withSource';
import { exportedItemPrice, exportedItemTitle } from './exportedItemPrice';

export type CartToExcelRow = {
  id: string;
  title: string;
  quantity: string | number;
  ccyS: string;
  price: number | null;
  ccy: string;
  sku: string | undefined;
  link: string;
};

export const exportCartToExcel = ({
  cartId,
  ccy: cartCCY,
  ccyS: cartCCYS,
  items,
  title,
  timestamp,
  store,
}: ExportableCart) => {
  const fileName = (title ? title + ' - ' + cartId : cartId) + '.xlsx';

  const targetLinks: string[] = [];

  const rows: CartToExcelRow[] = items.map((item) => {
    targetLinks.push(item.url);

    return {
      id: item.asin,
      title: exportedItemTitle(item),
      quantity: item.quantity || 0,
      ccyS: cartCCYS,
      price: exportedItemPrice(item),
      ccy: cartCCY,
      sku: item.skuString ? item.skuString : item.sku,
      // Forwarder paths get getRootUrl(); bare retailer paths must not become share-a-cart.com/products/...
      link: itemOutboundHref(item.url),
    };
  });

  // @ts-expect-error - TS error but still works.
  const ws = utils.json_to_sheet(rows, { origin: 'A5' });
  const wb = utils.book_new();

  for (let i = 6, j = 0; j < rows.length; i++, j++) {
    ws[`H${i}`].l = {
      Target: ws[`H${i}`].v,
    };
    if (rows[j].price !== null) ws[`E${i}`].z = '0.00';
  }

  // new workbook, worksheet from above, sheetname
  utils.book_append_sheet(wb, ws, 'Cart Items');

  // Header of sheet
  utils.sheet_add_aoa(ws, [['Share-A-Cart.com']], { origin: 'A1' });
  ws['A1'].l = {
    Target: `${getRootUrl()}`,
  };

  // Cart Timestamp
  utils.sheet_add_aoa(
    ws,
    [[`Cart Created: ${new Date(timestamp).toLocaleString()}`]],
    {
      origin: 'C1',
    },
  );

  if (store) {
    // Vendor Link
    utils.sheet_add_aoa(ws, [[`Cart Vendor: ${store}`]], {
      origin: 'C2',
    });
    ws['C2'].l = {
      Target: `${store}`,
    };
  }

  const pricesAsOf = cartPricesAsOf({ timestamp, items });
  if (pricesAsOf !== undefined) {
    utils.sheet_add_aoa(ws, [[`Prices as of: ${new Date(pricesAsOf).toLocaleString()}`]], {
      origin: 'C3',
    });
  }

  // Cart Name or Cart ID if cart.title doesn't exist
  utils.sheet_add_aoa(
    ws,
    [[`${title ? 'Cart Name: ' + title : 'Cart ID: ' + cartId}`]],
    {
      origin: 'A2',
    },
  );
  utils.sheet_add_aoa(ws, [[`${title ? 'Cart ID: ' + cartId : ''}`]], {
    origin: 'A3',
  });

  // Add cart link in appropriate row
  const rowToAddCartLink = title ? 'A3' : 'A2';
  ws[`${rowToAddCartLink}`].l = {
    Target: `${getRootUrl()}${localUrl(`/get/${cartId}`)}`,
  };

  utils.sheet_add_aoa(
    ws,
    [
      [
        'ID',
        'Title',
        'Quantity',
        'Price',
        'Price',
        'Currency',
        'SKU Data',
        'Link',
      ],
    ],
    {
      origin: 'A5',
    },
  );

  // Column Width
  const wscols = [
    {
      wch: 20,
    }, // ID
    {
      wch: 40,
    }, // Title
    {
      wch: 8,
    }, // Quantity
    {
      wch: 2,
    }, // ccyS - titled Price
    {
      wch: 8,
    }, // Price
    {
      wch: 8,
    }, // Currency
    {
      wch: 20,
    }, // SKU Data
    {
      wch: 40,
    }, // Link
  ];
  ws['!cols'] = wscols;

  // Cell Merging: s = start, e = end, r = row, c = column *0 based*
  const wsmerges = [
    {
      s: {
        r: 1,
        c: 2,
      },
      e: {
        r: 1,
        c: 6,
      },
    },
    {
      s: {
        r: 4,
        c: 3,
      },
      e: {
        r: 4,
        c: 4,
      },
    },
  ];
  ws['!merges'] = wsmerges;

  // Totals carry their computed value too, for viewers that don't recalculate
  const lastItemRow = rows.length + 5;
  const totalsRow = rows.length + 7;
  const totalQuantity = rows.reduce((sum, row) => sum + Number(row.quantity), 0);
  const totalPrice = rows.reduce((sum, row) => sum + (row.price ?? 0) * Number(row.quantity), 0);

  utils.sheet_add_aoa(ws, [['Total']], { origin: `B${totalsRow}` });
  ws[`C${totalsRow}`] = { t: 'n', v: totalQuantity, f: `SUM(C6:C${lastItemRow})` };
  ws[`E${totalsRow}`] = {
    t: 'n',
    v: totalPrice,
    f: `SUMPRODUCT(C6:C${lastItemRow},E6:E${lastItemRow})`,
    z: '0.00',
  };

  writeFile(wb, fileName);
};
